<!-- @format -->
<template>
    <div class="plain-text-email-templates-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ steps[step].title || 'Templates' }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div class="plain-text-templates">
                    <div v-if="clientId" class="template-section">
                        <label>Source</label>
                        <source-dropdown
                            v-model="source"
                            :client-id="clientId"
                            @input="selectSource($event)"
                        />
                    </div>
                    <div v-if="source" class="category-section">
                        <label>Category</label>
                        <plain-text-template-categories-search
                            v-if="selectedSubscriptionTemplates"
                            v-model="category"
                            :promo-id="promoId"
                        />
                        <plain-text-template-categories-search
                            v-else
                            v-model="category"
                            :agency-id="agencyId"
                            :client-id="clientId"
                        />
                    </div>
                    <div v-if="category" class="template-section">
                        <label>Template</label>
                        <plain-text-template-search
                            v-if="selectedSubscriptionTemplates"
                            v-model="template"
                            :promo-id="promoId"
                            :category-name="category.name"
                        />
                        <plain-text-template-search
                            v-else
                            v-model="template"
                            :category-name="category.name"
                            :client-id="clientId"
                            :agency-id="agencyId"
                        />
                    </div>
                    <div v-if="template" class="current-content">
                        <div v-show="template.id" class="current-content-wrapper">
                            <div class="title-container">
                                <h3>{{ template.name }}</h3>
                            </div>
                            <div class="subject-container">
                                <h4>Subject</h4>
                                <p>{{ template.subject }}</p>
                            </div>
                            <div class="message-container">
                                <h4>Content</h4>
                                <span v-html="template.body"></span>
                            </div>
                            <div
                                v-if="template.attachments.length > 0"
                                class="attachments-container"
                            >
                                <h4>Attachments</h4>
                                <div class="attachments">
                                    <div
                                        v-for="attachment in template.attachments"
                                        :key="attachment.collateral.id"
                                        class="attachment"
                                    >
                                        {{ attachment.collateral.name }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button
                        :loading="steps[step].loading"
                        :disabled="isButtonDisabled || steps[step].disabled"
                        @click="steps[step].action"
                    >
                        {{ steps[step].buttonText }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const PlainTextTemplateCategoriesSearch = () =>
    import('@/components/email-template/plain-text-template-categories-search');
const SourceDropdown = () => import('@/components/common/source-dropdown');
const PlainTextTemplateSearch = () =>
    import('@/components/email-template/plain-text-template-search');
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'plain-text-email-templates-widget',

    components: {
        'plain-text-template-categories-search': PlainTextTemplateCategoriesSearch,
        'plain-text-template-search': PlainTextTemplateSearch,
        'source-dropdown': SourceDropdown,
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        options: {
            type: Object,
            default: null,
        },

        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            editBeforeAdding: this.widget.editBeforeAdding ? this.widget.editBeforeAdding : false,
            agencyId: this.widget.agencyId,
            clientId: this.widget.clientId,
            promoId: null,

            steps: {
                template: {
                    title: 'Email Template',
                    buttonText: 'Add',
                    action: this.addPlainTextTemplate,
                    loading: false,
                },

                adding: {
                    title: 'Adding Template',
                    buttonText: 'Adding',
                    action: () => {},
                    loading: true,
                    disabled: true,
                },
            },

            step: 'template',

            source: null,
            category: null,
            template: null,
        };
    },

    computed: {
        isButtonDisabled() {
            return this.template && this.category ? false : true;
        },

        selectedSubscriptionTemplates() {
            return this.source && this.source.name === 'Included With Subscription';
        },
    },

    watch: {
        source(newValue, oldValue) {
            if (newValue != oldValue) {
                this.category = null;
                this.template = null;
            }
        },

        category(newValue, oldValue) {
            if (newValue != oldValue) {
                this.template = null;
            }
        },
    },

    created() {
        if (this.agencyId) {
            this.getAgency();
        } else if (this.clientId) {
            this.getPromoId();
        } else {
            this.setSourceToOrganization();
        }

        EventBus.$on('email-template:select', this.closeWidget);
    },

    beforeDestroy() {
        EventBus.$off('email-template:select', this.closeWidget);
    },

    methods: {
        setSourceToOrganization() {
            this.source = {
                id: -1,
                name: 'Organization',
            };
        },

        addPlainTextTemplate() {
            if (this.editBeforeAdding) {
                EventBus.$emit('open-widget', {
                    name: 'email-template-staging-widget',
                    options: {
                        emailTemplateId: this.template.id,
                        clientId: this.clientId,
                        agencyId: this.agencyId,
                    },
                });
            } else {
                this.step = 'adding';
                EventBus.$emit('email-template:select', this.template);
                this.closeWidget();
            }
        },

        selectSource(source) {
            this.agencyId = null;

            if (source.id && source.id > 0) {
                this.agencyId = source.id;
            }
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getAgency() {
            this.$api
                .get(`/service/Agencies?$filter=Id eq ${this.agencyId}`)
                .then((response) => {
                    this.source = {
                        id: response.data.value[0].Id,
                        name: response.data.value[0].Name,
                    };
                    this.selectSource(this.source);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while getting your vendor info.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isInitializing = false;
                });
        },

        getPromoId() {
            this.$api
                .get(`/service/Clients?$filter=Id eq ${this.clientId}&$select=PromoId`)
                .then((response) => {
                    this.promoId = response.data.value[0].PromoId;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while getting your templates.',
                        { error },
                    );
                });
        },
    },
};
</script>

<style>
.plain-text-email-templates-widget.widget .widget-header {
    background: rgb(80, 82, 254);
    background: linear-gradient(90deg, rgba(80, 82, 254, 1) 0%, rgba(75, 201, 254, 1) 100%);
}

.plain-text-email-templates-widget .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.plain-text-email-templates-widget .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.plain-text-email-templates-widget .plain-text-templates label {
    font-weight: 400;
}

.plain-text-email-templates-widget .category-section {
    margin-bottom: 15px;
}

.plain-text-email-templates-widget .template-section {
    padding-top: 10px;
    margin-bottom: 30px;
}

.plain-text-email-templates-widget .title-container h3 {
    margin-bottom: 18px;
}

.plain-text-email-templates-widget h4 {
    margin-bottom: 10px;
}

.plain-text-email-templates-widget .subject-container,
.plain-text-email-templates-widget .message-container {
    margin-bottom: 20px;
}

.plain-text-email-templates-widget .current-content p,
.plain-text-email-templates-widget .current-content span {
    color: #877d88;
    font-size: 16px;
}

.plain-text-email-templates-widget .attachments-container .attachments {
    display: flex;
    flex-wrap: wrap;
}

.plain-text-email-templates-widget .attachments-container .attachment {
    position: relative;
    padding: 4px 10px 4px 10px;
    margin-right: 10px;
    background-color: #fff;
    border-radius: 20px;
    box-shadow: 3px 3px 12px rgba(130, 116, 232, 0.2);
}

.plain-text-email-templates-widget .plain-text-templates .message-container ul {
    padding-left: 40px;
}

.plain-text-email-templates-widget .plain-text-templates .message-container ul li {
    list-style: disc;
}

.plain-text-email-templates-widget .plain-text-template-categories-search .multiselect__tags,
.plain-text-email-templates-widget .plain-text-template-categories-search .multiselect__option {
    text-transform: capitalize;
}
</style>
